import { ChevronDown, Lock } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Collapsible, CollapsibleContent } from '~/components/ui/collapsible';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { cn } from '~/utils/cn';

import { AttributePill } from './attribute-pill';
import type { EnrichmentAttributeGroup, EnrichmentAttributeOption } from './types';

interface AttributeGroupCardProps {
  group: EnrichmentAttributeGroup;
  /** Attributes to render — already narrowed by the active search query. */
  visibleAttributes: EnrichmentAttributeOption[];
  /** Attribute key → selected, for the whole taxonomy. */
  selected: Record<string, boolean>;
  /** Group locked because its required attribute is unselected. */
  locked: boolean;
  /** Label of the attribute this group needs, for the lock chip and reason line. */
  requiresLabel: string | null;
  expanded: boolean;
  onToggleGroup: () => void;
  onToggleExpand: () => void;
  onToggleAttribute: (key: string) => void;
  /** Resolves an attribute key to its display label for the reason copy. */
  labelForKey: (key: string) => string;
  /** `data-testid` for the card; derives `-checkbox`, `-expand-button`, `-count`, `-attribute-{key}`. */
  testId?: string;
}

/**
 * One attribute group: tri-state header checkbox, live `selected/total` count,
 * and a collapsible body of attribute pills. Groups start expanded.
 *
 * Reasons why something is locked are rendered as visible text under the pill
 * row and wired to the pills with `aria-describedby` — not as a hover tooltip,
 * which does not exist on touch and would make an inert control unexplainable.
 */
export function AttributeGroupCard({
  group,
  visibleAttributes,
  selected,
  locked,
  requiresLabel,
  expanded,
  onToggleGroup,
  onToggleExpand,
  onToggleAttribute,
  labelForKey,
  testId = `attribute-group-card-${group.slug}`,
}: AttributeGroupCardProps) {
  const { t } = useTranslation('common');
  const reasonBaseId = useId();

  const selectedCount = locked ? 0 : group.attributes.filter((a) => selected[a.key]).length;
  const total = group.attributes.length;
  // Single-choice group: the one selected option's key (or '' for none), used as
  // the controlled value of the RadioGroup rendered below.
  const selectedKeyInGroup = group.attributes.find((a) => selected[a.key])?.key ?? '';
  // Radix maps "indeterminate" to `aria-checked="mixed"`; a locked group reads as
  // fully unchecked because its attributes are all forced off.
  const headerChecked: boolean | 'indeterminate' =
    selectedCount === 0 ? false : selectedCount === total ? true : 'indeterminate';

  // Attribute-level dependencies that are unmet but *reachable* — the group
  // itself is unlocked, so naming the missing requirement is actionable. When the
  // whole group is locked the group-level reason already covers it.
  const unmetAttributes = locked
    ? []
    : visibleAttributes.filter((a) => a.requiresKey !== undefined && !selected[a.requiresKey]);

  // Unsupported on this surface — a permanent lock with its own chip and reason
  // copy, since the dependency wording ("turn X on") would be a lie here.
  const unavailable = group.unavailable === true;
  const groupReasonId = `${reasonBaseId}-group`;
  const attributeReasonId = `${reasonBaseId}-attributes`;
  const mandatoryReasonId = `${reasonBaseId}-mandatory`;
  // A group with any always-on attribute shows a single "can't be turned off" line.
  const hasMandatoryAttribute = visibleAttributes.some((a) => a.mandatory);

  return (
    <div data-testid={testId} className="overflow-hidden rounded-lg border">
      <div
        className={cn('flex items-center gap-3 px-4 py-3', locked ? 'bg-muted/60' : 'bg-muted/40')}
      >
        {/* Single-choice groups have no select-all — a radio set can't be "all on". */}
        {!group.singleSelect && (
          <Checkbox
            checked={headerChecked}
            disabled={locked}
            aria-label={t('enrichmentRun.attributes.toggleGroup', { group: group.label })}
            data-testid={`${testId}-checkbox`}
            {...(locked ? { 'aria-describedby': groupReasonId } : {})}
            className={cn(locked ? 'cursor-not-allowed' : 'cursor-pointer')}
            onCheckedChange={() => {
              onToggleGroup();
            }}
          />
        )}

        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            data-testid={`${testId}-label`}
            className={cn('text-sm font-semibold', locked && 'text-muted-foreground')}
          >
            {group.label}
          </span>
          {group.subLabel && (
            <span className="text-muted-foreground text-xs">{group.subLabel}</span>
          )}
          {group.badge !== undefined && !locked && (
            <span
              data-testid={`${testId}-badge`}
              className="bg-selected-row text-accent-main rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase"
            >
              {t(`enrichmentRun.groupBadge.${group.badge}`)}
            </span>
          )}
          {locked && (unavailable || requiresLabel !== null) && (
            <span
              data-testid={`${testId}-locked-badge`}
              className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold"
            >
              <Lock className="size-2.5" aria-hidden="true" />
              {unavailable
                ? t('enrichmentRun.attributes.unavailable')
                : t('enrichmentRun.attributes.needs', { attribute: requiresLabel })}
            </span>
          )}
        </div>

        <span
          data-testid={`${testId}-count`}
          className={cn(
            'ml-auto text-xs font-semibold tabular-nums',
            selectedCount > 0 ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {selectedCount} of {total}
        </span>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground h-7 w-7 shrink-0 cursor-pointer"
          aria-label={t('enrichmentRun.attributes.toggleExpand', { group: group.label })}
          aria-expanded={expanded}
          data-testid={`${testId}-expand-button`}
          onClick={onToggleExpand}
        >
          <ChevronDown
            aria-hidden="true"
            className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
          />
        </Button>
      </div>

      <Collapsible open={expanded}>
        <CollapsibleContent>
          <div className="space-y-2.5 border-t bg-white px-4 py-3.5">
            {group.singleSelect ? (
              <RadioGroup
                value={selectedKeyInGroup}
                disabled={locked}
                aria-label={group.label}
                data-testid={`${testId}-attributes`}
                onValueChange={(key) => {
                  onToggleAttribute(key);
                }}
              >
                {visibleAttributes.map((attribute) => {
                  const radioId = `${reasonBaseId}-radio-${attribute.key}`;
                  return (
                    <div key={attribute.key} className="flex items-center gap-2.5">
                      <RadioGroupItem
                        value={attribute.key}
                        id={radioId}
                        data-testid={`${testId}-attribute-${attribute.key}`}
                        disabled={locked}
                        className={cn('size-5', locked ? 'cursor-not-allowed' : 'cursor-pointer')}
                        {...(locked ? { 'aria-describedby': groupReasonId } : {})}
                      />
                      <Label
                        htmlFor={radioId}
                        className={cn(
                          'text-sm font-normal',
                          locked ? 'text-muted-foreground cursor-not-allowed' : 'cursor-pointer',
                        )}
                      >
                        {attribute.label}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            ) : (
              <div className="flex flex-wrap gap-2">
                {visibleAttributes.map((attribute) => {
                  const mandatory = !!attribute.mandatory;
                  const attributeLocked =
                    !mandatory &&
                    (locked ||
                      (attribute.requiresKey !== undefined && !selected[attribute.requiresKey]));
                  return (
                    <AttributePill
                      key={attribute.key}
                      label={attribute.label}
                      selected={!!selected[attribute.key]}
                      locked={attributeLocked}
                      mandatory={mandatory}
                      testId={`${testId}-attribute-${attribute.key}`}
                      {...(mandatory
                        ? {
                            describedBy: mandatoryReasonId,
                            tooltip: t('enrichmentRun.attributes.mandatoryTooltip'),
                          }
                        : attributeLocked
                          ? { describedBy: locked ? groupReasonId : attributeReasonId }
                          : {})}
                      onToggle={() => {
                        onToggleAttribute(attribute.key);
                      }}
                    />
                  );
                })}
              </div>
            )}

            {locked && unavailable && (
              <p id={groupReasonId} className="text-muted-foreground text-xs">
                {t(group.unavailableReasonKey ?? 'enrichmentRun.attributes.groupUnavailableReason')}
              </p>
            )}

            {locked && !unavailable && requiresLabel !== null && (
              <p id={groupReasonId} className="text-muted-foreground text-xs">
                {t('enrichmentRun.attributes.groupLockedReason', { attribute: requiresLabel })}
              </p>
            )}

            {unmetAttributes.length > 0 && (
              <p id={attributeReasonId} className="text-muted-foreground text-xs">
                {unmetAttributes
                  .map((a) =>
                    t('enrichmentRun.attributes.attributeLockedReason', {
                      requirement: labelForKey(a.requiresKey ?? ''),
                      attribute: a.label,
                    }),
                  )
                  .join(' ')}
              </p>
            )}

            {hasMandatoryAttribute && (
              <p id={mandatoryReasonId} className="text-muted-foreground text-xs">
                {t('enrichmentRun.attributes.mandatoryReason')}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
